import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { extractErrorMessage } from '@/shared/api/errors';
import { useArrivedPackageDetail } from '@/entities/warehouse-arrivals';
import { DeliveredToggle } from '@/features/warehouse-arrivals';
import { usePerm } from '@/shared/hooks/usePerm';

export const PagesWarehouseArrivedPackageMain = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const query = useArrivedPackageDetail(id);
  const canMarkDelivered = usePerm('warehouse.mark_delivered');

  if (query.isLoading) {
    return (
      <>
        <PageHeader
          title={t('warehouseArrivals.packages.viewTitle')}
          showBack
          backTo="/warehouse/arrived-packages"
        />
        <div className="space-y-3 px-4 py-4 lg:px-6 lg:py-6">
          <div className="h-10 animate-pulse rounded-lg bg-slate-200/70" />
          <div className="h-64 animate-pulse rounded-lg bg-slate-200/60" />
          <div className="flex items-center justify-center pt-4">
            <Spinner size="md" />
          </div>
        </div>
      </>
    );
  }

  const status =
    query.error instanceof AxiosError ? query.error.response?.status : undefined;
  if (status === 404 || (!query.error && !query.data)) {
    return (
      <>
        <PageHeader
          title={t('warehouseArrivals.packages.viewTitle')}
          showBack
          backTo="/warehouse/arrived-packages"
        />
        <div className="px-4 py-4 lg:px-6 lg:py-6">
          <ErrorState
            title={t('warehouseArrivals.packages.notFound')}
            message={t('warehouseArrivals.packages.notFound')}
          />
        </div>
      </>
    );
  }

  if (query.error || !query.data) {
    return (
      <>
        <PageHeader
          title={t('warehouseArrivals.packages.viewTitle')}
          showBack
          backTo="/warehouse/arrived-packages"
        />
        <div className="px-4 py-4 lg:px-6 lg:py-6">
          <ErrorState
            message={extractErrorMessage(
              query.error,
              t('warehouseArrivals.packages.loadDetailError'),
            )}
            onRetry={() => void query.refetch()}
          />
        </div>
      </>
    );
  }

  const data = query.data;

  return (
    <>
      <PageHeader
        title={t('warehouseArrivals.packages.viewTitle')}
        showBack
        backTo="/warehouse/arrived-packages"
      />
      <div className="px-4 py-4 lg:px-6 lg:py-6">
        <div className="rounded-xl border border-slate-200 bg-white p-4 lg:p-6">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.packages.fields.name')}
              </dt>
              <dd className="text-sm font-medium text-ink">{data.name || '—'}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.packages.fields.party')}
              </dt>
              <dd className="text-sm font-medium text-ink">{data.party?.name || '—'}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.packages.fields.price')}
              </dt>
              <dd className="text-sm font-medium text-ink">
                {data.price != null ? String(data.price) : '—'}
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.packages.fields.weight')}
              </dt>
              <dd className="text-sm font-medium text-ink">
                {data.weight != null
                  ? `${data.weight} ${data.weight_unit ?? ''}`.trim()
                  : '—'}
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.packages.fields.parcels')}
              </dt>
              <dd className="text-sm font-medium text-ink">
                {data.parcels.length
                  ? data.parcels.map((p) => p.trek_id || p.name || '—').join(', ')
                  : '—'}
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-sm text-ink-muted">
                {t('warehouseArrivals.packages.deliveredToCustomer')}
              </dt>
              <dd className="text-sm font-medium text-ink">
                <DeliveredToggle
                  packageId={data.id}
                  value={data.delivered_to_customer}
                  canEdit={canMarkDelivered}
                />
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </>
  );
};
